import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import ComicCard from "@/components/base/ComicCard";
import useSeo from "@/hooks/useSeo";
import { comics, categories } from "@/mocks/manga";

const features = [
  { icon: "ri-book-open-line", title: "翻页模式", desc: "像翻阅纸质漫画一样流畅翻页，还原真实阅读体验。" },
  { icon: "ri-smartphone-line", title: "横屏阅读", desc: "支持横屏浏览，大幅画面更沉浸，双页同开随心切换。" },
  { icon: "ri-moon-clear-line", title: "夜间模式", desc: "低亮度护眼配色，深夜追漫也不刺眼，舒适不疲惫。" },
  { icon: "ri-download-2-line", title: "缓存下载", desc: "一键缓存整话，离线也能看，通勤地铁尽情追漫。" },
];

const stats = [
  { value: "500+", label: "精选漫画" },
  { value: "1200万+", label: "注册用户" },
  { value: "30亿+", label: "累计阅读量" },
  { value: "98%", label: "用户好评率" },
];

const ranking = [...comics].sort((a, b) => b.popularity - a.popularity).slice(0, 8);

const faqs = [
  { q: "漫悦漫画网支持哪些阅读模式？", a: "支持漫画翻页模式、横屏阅读与卷动阅读，阅读设置可在阅读器中自由调节，满足不同读者的追漫习惯。" },
  { q: "如何开启漫画夜间模式？", a: "进入阅读器后点击右上角的月亮图标，即可一键切换为护眼的夜间模式，深夜追漫更舒适。" },
  { q: "漫画可以缓存下载离线观看吗？", a: "可以。在阅读器中点击下载按钮即可缓存当前章节，离线状态下也能流畅阅读。" },
  { q: "漫画画质如何？会收费吗？", a: "本站漫画均为高清原图，画质清晰。绝大多数漫画可免费阅读，部分精品内容以会员制提供。" },
];

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "漫悦漫画网",
  url: "/",
  description:
    "支持漫画翻页模式、横屏阅读、夜间模式与缓存下载的在线漫画阅读平台。",
  inLanguage: "zh-CN",
};

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "漫悦漫画网",
  url: "/",
  logo: "/vite.svg",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "400-820-2026",
    contactType: "customer service",
    areaServed: "CN",
    availableLanguage: "Chinese",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const webpageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "漫悦漫画网 - 在线漫画阅读器",
  url: "/",
  inLanguage: "zh-CN",
  about: "在线漫画阅读平台",
};

export default function Home() {
  useSeo({
    title: "在线漫画阅读器 - 翻页模式·横屏阅读·夜间模式·缓存下载",
    description:
      "漫悦漫画网是专业的在线漫画阅读器，支持漫画翻页模式、横屏阅读、夜间模式与缓存下载，阅读设置自由调节，翻页流畅画质清晰，为追漫体验加分。",
    keywords: "漫画,漫画阅读器,漫画翻页模式,漫画横屏阅读,漫画夜间模式,漫画缓存下载,漫画阅读设置",
    path: "/",
    jsonLd: [websiteSchema, orgSchema, faqSchema, webpageSchema],
  });

  return (
    <Layout>
      {/* Hero */}
      <section className="relative flex min-h-[540px] items-center overflow-hidden bg-background-950">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20anime%20manga%20collage%20hero%20banner%20of%20colorful%20comic%20characters%20in%20dynamic%20action%20poses%20with%20speed%20lines%20and%20comic%20halftone%20dots%2C%20warm%20orange%20and%20gold%20accent%20lighting%20on%20deep%20charcoal%20background%2C%20artistic%20abstract%20comic%20book%20artwork%2C%20cinematic%20and%20energetic&width=1600&height=900&seq=manga-hero-01&orientation=landscape"
          alt="漫悦漫画网 在线漫画阅读平台"
          title="漫画阅读器 在线漫画平台"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/60 to-background-950/90" />

        <div className="relative mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
          <div className="max-w-2xl">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-accent-500/20 px-3 py-1 text-xs font-medium text-accent-500 ring-1 ring-accent-500/40">
              <i className="ri-fire-line" aria-hidden="true" />
              全新阅读器 · 追漫体验全面升级
            </p>
            <h1 className="font-heading text-3xl leading-tight text-background-50 md:text-5xl">
              翻页流畅 · 画质清晰
              <br />
              <span className="text-accent-500">为追漫体验加分</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-background-100/90 md:text-lg">
              漫悦漫画网阅读器功能全面升级，支持漫画翻页模式与横屏阅读，漫画夜间模式护眼舒适，漫画缓存下载离线也能看，个性化阅读设置随心调节。
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/categories"
                className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                开始追漫
                <i className="ri-arrow-right-line ml-2" aria-hidden="true" />
              </Link>
              <Link
                to="/categories"
                className="inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 ring-1 ring-background-50/30 transition-colors hover:bg-background-50/20"
              >
                浏览分类
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label} className="rounded-lg bg-background-50/5 p-3 ring-1 ring-background-50/10">
                  <p className="font-heading text-xl text-accent-500 md:text-2xl">{s.value}</p>
                  <p className="mt-1 text-xs text-background-100/80">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 热门连载 */}
      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest text-primary-600">热门连载</p>
              <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">本周大家都在追</h2>
            </div>
            <Link to="/categories" className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
              查看全部 <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
            {comics.filter((c) => c.featured).map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </section>

      {/* 分类入口 */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-8 text-center">
            <p className="text-xs font-medium uppercase tracking-widest text-primary-600">分类浏览</p>
            <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">海量分类 · 总有你爱的</h2>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {categories.slice(1).map((c) => {
              const count = comics.filter((x) => x.category === c.id).length;
              return (
                <Link
                  key={c.id}
                  to={`/categories?cat=${c.id}`}
                  className="group flex cursor-pointer flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-6 transition-all duration-300 hover:border-primary-300 hover:bg-background-50"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                    <i className="ri-command-line text-2xl" aria-hidden="true" />
                  </span>
                  <div className="text-center">
                    <p className="font-bold text-foreground-950">{c.name}</p>
                    <p className="mt-1 text-xs text-foreground-500">{count} 部作品</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 阅读功能亮点 */}
      <section className="relative overflow-hidden bg-background-950 py-14 md:py-20">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20abstract%20comic%20style%20background%20with%20flowing%20ink%20ribbons%20in%20orange%20gold%20and%20teal%20on%20deep%20black%2C%20subtle%20halftone%20patterns%2C%20artistic%20manga%20texture%2C%20elegant%20and%20modern&width=1600&height=900&seq=manga-bg-01&orientation=landscape"
          alt="漫画阅读功能背景"
          title="漫画阅读功能升级"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/80 via-background-950/70 to-background-950/90" />

        <div className="relative mx-auto max-w-7xl px-4 py-6 md:px-6">
          <div className="mb-10 text-center">
            <p className="text-xs font-medium uppercase tracking-widest text-accent-500">阅读设置 · 自由调节</p>
            <h2 className="mt-1 font-heading text-2xl text-background-50 md:text-3xl">四大模式，畅享沉浸追漫</h2>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="group rounded-lg border border-background-50/10 bg-background-50/5 p-6 transition-colors hover:border-accent-500/40"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500 text-background-950">
                  <i className={`${f.icon} text-xl`} aria-hidden="true" />
                </span>
                <p className="mt-4 font-heading text-lg text-background-50">{f.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-background-100/80">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 排行榜 */}
      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest text-primary-600">人气榜单</p>
              <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">实时热度排行榜</h2>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {ranking.map((c, idx) => (
              <Link
                key={c.id}
                to={`/comic/${c.id}`}
                className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-lg ${
                    idx < 3 ? "bg-accent-500 text-background-950" : "bg-secondary-100 text-secondary-900"
                  }`}
                >
                  {idx + 1}
                </span>
                <img
                  src={c.cover}
                  alt={`${c.title} 封面`}
                  title={`${c.title} 漫画`}
                  className="h-16 w-12 shrink-0 rounded-md object-cover object-top"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold text-foreground-950 group-hover:text-primary-600">{c.title}</p>
                  <p className="mt-0.5 truncate text-xs text-foreground-500">{c.author} · {c.categoryName}</p>
                </div>
                <div className="text-right">
                  <p className="flex items-center justify-end gap-1 text-sm font-semibold text-accent-600">
                    <i className="ri-star-fill" aria-hidden="true" />
                    {c.rating}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">{c.views}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <div className="mb-8 text-center">
            <p className="text-xs font-medium uppercase tracking-widest text-primary-600">常见问题</p>
            <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">追漫小贴士</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f) => (
              <details
                key={f.q}
                className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors open:border-primary-300"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-foreground-950">
                  {f.q}
                  <i className="ri-add-line text-lg text-primary-600 transition-transform group-open:rotate-45" aria-hidden="true" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="relative overflow-hidden rounded-lg bg-primary-500 px-6 py-12 text-center md:px-12">
            <i className="ri-book-open-line absolute -left-4 -top-4 text-8xl text-background-50/10" aria-hidden="true" />
            <i className="ri-sparkling-2-line absolute -bottom-4 -right-4 text-8xl text-background-50/10" aria-hidden="true" />
            <h2 className="font-heading text-2xl text-background-50 md:text-3xl">开启你的沉浸式追漫之旅</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-background-50/90 md:text-base">
              翻页流畅、画质清晰、夜间护眼、离线可看，漫悦漫画网让每一部好漫画都不错过。
            </p>
            <Link
              to="/categories"
              className="mt-7 inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-background-50 px-7 py-3 text-sm font-semibold text-primary-600 transition-colors hover:bg-background-100"
            >
              立即开始追漫
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}